import { MAX_AMOUNT_CENTS } from './constants/index.ts';
import { currencyStepCents, minimumChargeCents } from './currency.ts';

export interface VisitPriceAdjustment {
  label: string;
  /** Final, tax-inclusive change to this visit, in stored hundredths. */
  amountCents: number;
  /**
   * The saved add-on this line was filled from (migration 0093), kept for
   * reporting. The label is the line's own copy: renaming or archiving the
   * add-on later does not change what this visit says it sold.
   */
  addOnId?: string;
}

export interface VisitPricing {
  appointmentId: string;
  revision: number;
  basePriceCents: number | null;
  priceCents: number | null;
  currencyCode: string | null;
  adjustments: VisitPriceAdjustment[];
  reason: string | null;
  editable: boolean;
  /**
   * After a save only: what happened to the checklist tasks of the saved
   * add-ons on this visit. `added` went onto this visit's checklist now;
   * `skipped` could not, because the visit's checklist has already started.
   */
  checklistTasks?: { added: string[]; skipped: string[] };
  blockedReason:
    | 'unpriced'
    | 'closed'
    | 'money_started'
    | 'currency_changed'
    | 'price_changed'
    | 'range_quote'
    | 'single_job'
    | null;
}

export interface VisitPricingUpdate {
  revision: number;
  requestId: string;
  expectedPriceCents: number;
  currencyCode: string;
  basePriceCents: number;
  adjustments: VisitPriceAdjustment[];
  reason: string;
}

/** One absolute replacement, never an increment over the previous total. */
export function adjustedVisitPrice(
  base: number,
  adjustments: readonly VisitPriceAdjustment[],
  currency: string,
): number {
  const step = currencyStepCents(currency);
  const valid = (amount: number) =>
    Number.isSafeInteger(amount) && Math.abs(amount) <= MAX_AMOUNT_CENTS && amount % step === 0;
  if (!valid(base) || base < 0 || adjustments.some((line) => !valid(line.amountCents))) {
    throw new Error('Enter amounts in the currency precision shown.');
  }
  const total = adjustments.reduce((sum, line) => sum + BigInt(line.amountCents), BigInt(base));
  if (
    total < 0n ||
    total > BigInt(MAX_AMOUNT_CENTS) ||
    (total !== 0n && total < BigInt(minimumChargeCents(currency)))
  ) {
    throw new Error('The final visit price is outside the supported range.');
  }
  return Number(total);
}

/** One line of a visit's invoice: the visit itself (`label` null) or one named price line. */
export interface VisitInvoiceLine {
  label: string | null;
  amountCents: number;
  addOnId: string | null;
}

/**
 * The lines a visit's invoice shows, from its current price breakdown: the
 * base visit first, then each add-on or price change by name, in the order
 * they were entered. Empty when the visit has no breakdown (or none that
 * still matches its price), and the invoice then shows the visit as one line.
 *
 * The lines always add up to `priceCents`; a breakdown that does not is
 * history from before the price was changed elsewhere and is not shown.
 */
export function visitInvoiceLines(
  priceCents: number | null,
  basePriceCents: number | null,
  adjustments: readonly VisitPriceAdjustment[] | null,
): VisitInvoiceLine[] {
  if (priceCents === null || basePriceCents === null || !adjustments?.length) return [];
  const total = adjustments.reduce((sum, line) => sum + line.amountCents, basePriceCents);
  if (total !== priceCents) return [];
  return [
    { label: null, amountCents: basePriceCents, addOnId: null },
    ...adjustments.map((line) => ({ label: line.label, amountCents: line.amountCents, addOnId: line.addOnId ?? null })),
  ];
}

/** Reads a stored `adjustments_json`; anything unreadable is no breakdown at all. */
export function parseVisitAdjustments(raw: string | null | undefined): VisitPriceAdjustment[] | null {
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const lines: VisitPriceAdjustment[] = [];
    for (const line of parsed) {
      if (!line || typeof line !== 'object') return null;
      const { label, amountCents, addOnId } = line as Record<string, unknown>;
      if (typeof label !== 'string' || typeof amountCents !== 'number' || !Number.isSafeInteger(amountCents))
        return null;
      lines.push(typeof addOnId === 'string' ? { label, amountCents, addOnId } : { label, amountCents });
    }
    return lines;
  } catch {
    return null;
  }
}
